/* eslint-disable no-restricted-imports */
import './App.css';

import '@sendbird/uikit-react/dist/index.css';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import NotFound404Page from '@pages/error/NotFound404Page';
import useAuth from '@src/stores/authStore';
import { darkTheme, lightTheme } from '@styles/theme';
import { ThemeProvider } from '@emotion/react';
import useTheme from '@stores/themeStore';
import { useMatchMedia } from '@hooks/useMatchMedia';
import ViewWhenNotPcSize from '@components/common/ViewWhenNotPcSize';
import useLanguageCodeStore from '@stores/languageCodeStore';
import type { LanguageCodes } from '@i18n/index';
import strings from '@i18n/index';
import { Suspense, lazy, useEffect, useState } from 'react';
import { KEY } from '@consts/keys';
import { loginToDashboardApi } from '@apis/auth';
import { clientOption } from '@apis/option';
import * as CryptoJS from 'crypto-js';
import { userProfileApi } from '@apis/user';
import useSendbirdApi from '@hooks/useSendbirdApi';
import useChannelListQueryStore from '@stores/channelListQueryStore';
import useMessageStore from '@stores/messageStore';
import OutOfChatModalPortal from '@components/modal/OutOfChatModalPortal';
import { useErrorHandler } from '@hooks/useErrorHandler';

const ChatMainPage = lazy(() => import('@pages/chat/ChatMainPage'));

const LoginPage = lazy(() => import('@pages/login/LoginPage'));

// 진입 시점에 확인해야 하는 사항
// 1. 대시보드를 통해 들어온 경우, userId 존재
// 2. 구성원을 통해 들어온 경우, userId, lang, targetUserId
// 3. userId, lang, targetUserId

const App = () => {
  const { userInfo, saveUser, logout } = useAuth();
  const { connect, sendbirdChat } = useSendbirdApi();
  const { setUserIds } = useChannelListQueryStore();
  const [isLoading, setIsLoading] = useState(true);
  const themeStore = useTheme();
  const { changeLanguageCode } = useLanguageCodeStore();
  const navigate = useNavigate();
  const isMobile = useMatchMedia();
  const { clearAll } = useMessageStore();
  const { showServerError } = useErrorHandler();

  const init = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const userId = params.get('id');
      const targetLanguage = params.get('lang');
      const targetUserId = params.get('inviteUserId') ?? '';

      // MARK 기존 로컬스토리지에 있는 유저와 동일하지 않을 시 로그아웃 후 로그인
      const loginAuthInfo =
        localStorage.getItem(KEY.LOGIN_AUTH) && JSON.parse(localStorage.getItem(KEY.LOGIN_AUTH) || '');

      if (loginAuthInfo && loginAuthInfo?.userInfo) {
        if (userId !== loginAuthInfo?.userInfo?.userId) {
          clearAll();
          logout();
          navigate('/login', {
            replace: true,
          });
          return;
        }
      }

      const lanCode = localStorage.getItem(KEY.LANGUAGE) as LanguageCodes | undefined;

      strings.setLanguage(lanCode || 'ko');
      changeLanguageCode(lanCode || 'ko');

      if (!userId && !userInfo) {
        navigate('/login', {
          replace: true,
        });
        return;
      }

      if (targetLanguage) {
        strings.setLanguage(targetLanguage || 'ko');
        changeLanguageCode((targetLanguage as LanguageCodes) || 'ko');
      }

      if (targetUserId) {
        sessionStorage.setItem('targetInviteUserId', targetUserId);
        setUserIds(targetUserId);
      }

      setIsLoading(true);

      const response = await loginToDashboardApi(userId || userInfo?.userId || '');

      if (response.data.result === '20000') {
        const clientResponse = await clientOption();
        localStorage.setItem(
          KEY.API_ID,
          CryptoJS.AES.encrypt(clientResponse.data?.body?.chatKey, clientResponse.data?.body?.clientId).toString(),
        );
        localStorage.setItem(KEY.CLIENT_ID, clientResponse.data?.body?.clientId);

        const userInfoRes = await userProfileApi();
        const { result: userResult, body } = userInfoRes.data;
        if (userResult === '20000') {
          const userInfo = body;

          if (userInfo) {
            saveUser(userInfo);

            await connect(userInfo.userId);
            await sendbirdChat.updateCurrentUserInfo({
              profileUrl: `${import.meta.env.VITE_APP_API_URL}${userInfo?.userRegImg}` || '',
              nickname: userInfo.userName,
            });

            navigate('/', {
              replace: true,
            });
          }
        }
      }
    } catch (e) {
      // MARK : 전역 error 핸들러 작업 후, 아래에 넣어주세요.
      showServerError('outOfChat');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routeMain = () => {
    return (
      <Routes>
        {userInfo ? (
          <>
            <Route path={`/`} element={<ChatMainPage />} />
            <Route path='*' element={<NotFound404Page />} />
          </>
        ) : (
          <Route path={`*`} element={<Navigate to={`/login`} replace />} />
        )}
      </Routes>
    );
  };

  // MARK : sendbird의 Loader type이 잘못 명시되어 있어 사용 시 에러가 납니다 (children이 필수값으로 명시되어 있음). Loading component 작업 후, 아래에 넣어주세요.
  if (isLoading) {
    return <div></div>;
  }

  return (
    // Mark :::: 바로 챗으로 진입하지 않아서, 채팅 스켈레톤으로 fallback을 그리는게 애매함(로그인 페이지로 갈 수도 있음)
    <Suspense fallback={<div></div>}>
      <ThemeProvider
        theme={{
          colors: themeStore.theme === 'dark' ? darkTheme : lightTheme,
        }}
      >
        {isMobile ? (
          <ViewWhenNotPcSize />
        ) : (
          <div className='App'>
            <Routes>
              <Route path='/error/404' element={<NotFound404Page />} />
              {!userInfo && <Route path='/login' element={<LoginPage />} />}
              <Route path='*' element={routeMain()} />
            </Routes>
          </div>
        )}

        <OutOfChatModalPortal />
      </ThemeProvider>
    </Suspense>
  );
};

export default App;
